import appConfiguration from "@/components/cfg/AppCfg";
import { APIValues } from "@/lib/api";
import {
  Filter,
  FilterOperator,
  Filters,
  processFilterValue,
} from "@/lib/filters";
import { ColumnSort } from "@tanstack/react-table";
import { OfflineQueueService } from "./offlineQueue";
import { NetworkDetectorService } from "./networkDetector";

export interface SuccessMutateItemResponse {
  status: string;
  id: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public errorCode: number,
    public responseBody?: string,
  ) {
    super(message);
  }
}

export interface SuccessFetchListResponse<T> {
  items: T[];
  count: number;
  last_item?: string;
}

export interface SuccessUpdateItemResponse<T> {
  ok: string;
  data: T;
}

export interface FetchListRequestParams<P> {
  limit?: number;
  lastItem?: string;
  filters?: Filters<P>;
  sorting?: ColumnSort;
  queryParams?: Record<string, string>;
}

export type FetchListRequest<
  P,
  C extends Record<string, string> = Record<string, string>,
> = FetchListRequestParams<P> & { queryParams?: C };

export class APIService<Read, Write = Read> {
  constructor(
    public path: APIValues,
    private offlineQueue?: OfflineQueueService,
    private networkDetector?: NetworkDetectorService,
  ) {}

  // Fetches a paginated list of items with optional filtering, sorting, and custom query params
  getList = async <
    Response = SuccessFetchListResponse<Read>,
    Custom extends Record<string, string> = Record<string, string>,
  >(
    {
      limit = appConfiguration.paginationLimit,
      lastItem,
      filters,
      sorting,
      queryParams,
    }: FetchListRequest<Read, Custom> = {},
    path = "",
  ): Promise<Response> => {
    const url = this.getListUrl(
      { limit, lastItem, filters, sorting, queryParams },
      path,
    );
    return this.performGetRequest<Response>(url);
  };

  // Fetches a static list without pagination or filtering (for simple data)
  getStaticList = async <Response = SuccessFetchListResponse<Read>>(
    path = "",
  ): Promise<Response> => {
    const url = this.getStaticListUrl(path);
    return this.performGetRequest<Response>(url);
  };

  // Fetches a single item by ID or the base resource
  get = async <IRead = Read>(value?: string): Promise<IRead> => {
    const url = value ? `${this.path}/${value}` : this.path;
    return this.performGetRequest<IRead>(url);
  };

  // Creates a new item, supports offline queuing
  post = async <T = Write, R = Read>(
    item: T,
    subPath?: string,
    queryParams?: Record<string, string>,
  ) => {
    const url = new URL(subPath ? `${this.path}/${subPath}` : `${this.path}`);
    this.appendSearchParamsToUrl(url, queryParams);

    if (!this.isOnline() && this.offlineQueue) {
      // Queue the request for offline processing
      const queueId = await this.offlineQueue.queueRequest(
        url,
        "POST",
        this.getDefaultHeaders(),
        item,
      );
      return { id: undefined, queueId } as R;
    }

    const response = await this.performFetch(url.toString(), "POST", item);
    const json =
      await this.handleResponse<SuccessUpdateItemResponse<R>>(response);
    return json.data;
  };

  // Updates an existing item by ID, supports offline queuing
  patch = async <T = Write, R = Read>(
    id: string,
    item: T,
    subPath?: string,
  ) => {
    const url = subPath
      ? `${this.path}/${id}/${subPath}`
      : `${this.path}/${id}`;

    if (!this.isOnline() && this.offlineQueue) {
      const queueId = await this.offlineQueue.queueRequest(
        url,
        "PATCH",
        this.getDefaultHeaders(),
        item,
      );

      // Return the patch data with id and queueId
      // The calling code should handle merging with current data if needed
      return {
        id,
        queueId,
        ...item,
      } as R;
    }

    const response = await this.performFetch(url, "PATCH", item);
    const json =
      await this.handleResponse<SuccessUpdateItemResponse<R>>(response);
    return json.data;
  };

  // TODO: the new API service should not expect PUT or PATCH to
  // have an as first arg, it should be more flexible
  put = async <T = Write, R = Read>(item: T, subPath?: string) => {
    const url = subPath ? `${this.path}/${subPath}` : this.path;

    const response = await this.performFetch(url, "PUT", item);
    const json =
      await this.handleResponse<SuccessUpdateItemResponse<R>>(response);
    return json.data;
  };

  // Deletes an item by ID, supports offline queuing
  delete = async <T = Write>(
    id: string,
    item?: T,
    subPath?: string,
    queryParams?: Record<string, string>,
  ) => {
    const url = this.buildUrl(
      subPath ? `${this.path}/${id}/${subPath}` : `${this.path}/${id}`,
    );

    this.appendSearchParamsToUrl(url, queryParams);

    if (!this.isOnline() && this.offlineQueue) {
      // Always queue delete operations for offline, using item if provided or a minimal payload
      const deletePayload = item || ({ id } as T);
      await this.offlineQueue.queueRequest(
        url.href,
        "DELETE",
        this.getDefaultHeaders(),
        deletePayload,
      );
      return;
    }

    const response = await this.performFetch(url.href, "DELETE", item);
    await this.handleResponse<undefined>(response);
  };

  // Performs a GET request with error handling
  private async performGetRequest<T>(url: string): Promise<T> {
    const validatedUrl = this.buildUrl(url);
    const response = await fetch(validatedUrl.href, {
      method: "GET",
      headers: this.getDefaultHeaders(),
    });

    return await this.handleResponse<T>(response);
  }

  // Performs POST/PATCH/DELETE requests with JSON body
  public async performFetch<T = Write>(
    url: string,
    method: "POST" | "PATCH" | "DELETE" | "PUT",
    body?: T,
  ): Promise<Response> {
    const validatedUrl = this.buildUrl(url);
    return fetch(validatedUrl.href, {
      method,
      headers: this.getDefaultHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // Handles response parsing and error checking
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      await this.handleResponseError(response);
    }

    // Handle empty responses (like for DELETE operations)
    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return undefined as T;
    }

    try {
      return await response.json();
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : "Invalid JSON response",
        response.status,
      );
    }
  }

  // Validates and builds URL from path, handles both relative and absolute URLs
  private buildUrl(path: string): URL {
    try {
      // Handle both absolute URLs and relative paths
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return new URL(path);
      }
      return new URL(path, window.location.origin);
    } catch (error) {
      throw new ApiError(
        error instanceof Error
          ? error.message
          : `Invalid URL construction: ${path}`,
        400,
      );
    }
  }

  // Checks if the device is online using network detector
  private isOnline(): boolean {
    return this.networkDetector?.getStatus().isOnline ?? true;
  }

  // Builds URL for list requests with pagination, filters, and sorting
  private getListUrl = <Custom extends Record<string, string>>(
    {
      limit,
      filters,
      lastItem,
      sorting,
      queryParams,
    }: FetchListRequest<Read, Custom>,
    path: string,
  ) => {
    const url = new URL(`${this.path}${path}`);
    url.searchParams.append("limit", String(limit));

    this.appendSearchParamsToUrl(url, queryParams);
    this.appendFiltersToUrl(url, filters);
    this.appendSortingToUrl(url, sorting);

    lastItem && url.searchParams.append("last_item", lastItem);

    return url.href;
  };

  // Builds simple URL for static list requests
  private getStaticListUrl = (path = "") => {
    return `${this.path}${path}`;
  };

  // Adds sorting parameters to URL
  private appendSortingToUrl(url: URL, sorting: ColumnSort | undefined) {
    if (sorting && sorting.id) {
      if (sorting.desc) {
        url.searchParams.append("sort", `-${sorting.id}`);
      } else {
        url.searchParams.append("sort", sorting.id);
      }
    }
  }

  // Adds custom query parameters to URL
  private appendSearchParamsToUrl(
    url: URL,
    queryParams: Record<string, string> | undefined,
  ) {
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) =>
        url.searchParams.append(key, value),
      );
    }
  }

  // Adds filter parameters to URL
  private appendFiltersToUrl(url: URL, filters: Filters<Read> | undefined) {
    if (!filters) return;

    filters.forEach(({ id, value }) => {
      this.appendSingleFilter(url, id, value);
    });
  }

  // Processes and adds a single filter to URL
  private appendSingleFilter(url: URL, key: string, { filter, value }: Filter) {
    const filterKey = filter ? `${key}[${filter}]` : key;

    if (value === undefined) {
      url.searchParams.append(filterKey, "");
      return;
    }

    // Convert non-string values to prevent type problems
    const stringValue = String(value);

    // Handle complex vs simple values
    if (this.hasFilterOperators(stringValue)) {
      this.appendComplexFilterValue(url, filterKey, stringValue);
    } else {
      url.searchParams.append(filterKey, stringValue.trim());
    }
  }

  // Checks if filter value contains AND/OR operators
  private hasFilterOperators(value: string): boolean {
    const operators = [FilterOperator.AND, FilterOperator.OR];
    return operators.some((operator) => value.includes(operator));
  }

  // Handles complex filter values with operators (AND/OR)
  private appendComplexFilterValue(url: URL, filterKey: string, value: string) {
    const deduplicatedValues = new Set(processFilterValue(value));
    deduplicatedValues.forEach((val) =>
      url.searchParams.append(filterKey, val.trim()),
    );
  }

  // Returns default headers with Content-Type
  private getDefaultHeaders() {
    return {
      "Content-Type": "application/json",
    };
  }

  public async handleResponseError(response: Response) {
    if (!response.ok) {
      const body = await response.json();
      if (typeof body.message == "string") {
        throw new ApiError(body.message, response.status);
      }
      let message;
      switch (response.status) {
        case 400:
          message = "Bad Request - Invalid data provided.";
          break;
        case 401:
          message = "Unauthorized - Please log in.";
          break;
        case 403:
          message = "Forbidden - You don't have permission.";
          break;
        case 404:
          message = "Not Found - The requested resource was not found.";
          break;
        case 429:
          message = "Too Many Requests - Please slow down.";
          break;
        case 500:
          message = "Internal Server Error - Try again later.";
          break;
        case 502:
          message = "Bad Gateway - Service temporarily unavailable.";
          break;
        case 503:
          message = "Service Unavailable - Please try again later.";
          break;
        case 504:
          message = "Gateway Timeout - Service took too long to respond.";
          break;
        default:
          message = `Unexpected HTTP error occurred. Error status: ${response.status}`;
          break;
      }
      throw new ApiError(message, response.status);
    }
  }
}
